import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import './AcceptInvitation.css';

// SVG Icons
const MailIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const AlertIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [userExists, setUserExists] = useState(false);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Form state for new users
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');

  // Form state for existing users
  const [loginPassword, setLoginPassword] = useState('');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`/team/validate/${token}`);

      if (response.data.success) {
        setInvitation(response.data.invitation);
        setUserExists(response.data.userExists);
      }
    } catch (err) {
      console.error('Error validating invitation:', err);
      setError(err.response?.data?.error || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAsNewUser = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setAccepting(true);
    setError('');

    try {
      const response = await axios.post(`/team/accept/${token}`, {
        password,
        username: username || invitation.email.split('@')[0]
      });

      if (response.data.success) {
        // Success - now log them in automatically
        try {
          const loginResponse = await axios.post('/merchant/auth/login', {
            email: invitation.email,
            password: password
          });

          if (loginResponse.data.success) {
            showToast('Welcome to the team! Taking you to your overview…', 'success');
            // Force a full page reload to ensure auth context updates
            window.location.href = '/overview';
          }
        } catch (loginErr) {
          console.error('Auto-login failed:', loginErr);
          showToast('Account created successfully! Please login with your credentials.', 'info');
          navigate('/login', { state: { email: invitation.email } });
        }
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleAcceptAsExistingUser = async (e) => {
    e.preventDefault();

    if (!loginPassword) {
      setError('Please enter your password');
      return;
    }

    setAccepting(true);
    setError('');

    // Step 1: Login first
    try {
      const loginResponse = await axios.post('/merchant/auth/login', {
        email: invitation.email,
        password: loginPassword
      });

      if (!loginResponse.data.success) {
        setError('Login failed. Please check your password.');
        setAccepting(false);
        return;
      }
    } catch (loginErr) {
      console.error('Login error:', loginErr);
      const loginError = loginErr.response?.data?.error || 'Invalid password';
      setError(loginError);
      setAccepting(false);
      return;
    }

    // Step 2: Accept invitation (only if login succeeded)
    try {
      const acceptResponse = await axios.post(`/team/accept/${token}`, {});

      if (acceptResponse.data.success) {
        showToast('Invitation accepted! Taking you to your overview…', 'success');
        // Force a full page reload to ensure auth context updates
        window.location.href = '/overview';
      } else {
        setError(acceptResponse.data.error || 'Failed to accept invitation');
      }
    } catch (acceptErr) {
      console.error('Accept error:', acceptErr);
      setError(acceptErr.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="invite-page">
        <div className="invite-card">
          <div className="invite-loading">
            <div className="invite-spinner" />
            <p>Verifying invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="invite-page">
        <div className="invite-card">
          <div className="invite-error-state">
            <div className="invite-error-icon">
              <AlertIcon />
            </div>
            <h2>Invitation Not Valid</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/login')} className="invite-btn invite-btn--primary">
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-page">
      <div className="invite-card">
        {/* Header */}
        <div className="invite-header">
          <div className="invite-icon">
            <MailIcon />
          </div>
          <h1>Team Invitation</h1>
        </div>

        {/* Invitation Details */}
        <div className="invite-details">
          <p className="invite-from">
            <strong>{invitation?.inviterName}</strong> has invited you to join
          </p>
          <h2 className="invite-store">{invitation?.storeName}</h2>
          <div className="invite-role">
            <UsersIcon />
            <span>{invitation?.role}</span>
          </div>
        </div>

        {/* Error Message */}
        {error && <div className="invite-error">{error}</div>}

        {userExists ? (
          // Existing user - login form
          <div className="invite-form-section">
            <h3>Login to Accept</h3>
            <p className="invite-form-desc">
              You already have an account. Enter your password to accept this invitation.
            </p>

            <form onSubmit={handleAcceptAsExistingUser}>
              <div className="invite-field">
                <label>Email</label>
                <input
                  type="email"
                  value={invitation?.email}
                  disabled
                  className="invite-input invite-input--disabled"
                />
              </div>

              <div className="invite-field">
                <label>Password</label>
                <div className="invite-input-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="invite-input"
                    required
                  />
                  <button
                    type="button"
                    className="invite-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="invite-btn invite-btn--primary"
                disabled={accepting}
              >
                {accepting ? (
                  <span className="invite-btn-loading" />
                ) : (
                  'Login & Accept Invitation'
                )}
              </button>
            </form>
          </div>
        ) : (
          // New user - registration form
          <div className="invite-form-section">
            <h3>Create Your Account</h3>
            <p className="invite-form-desc">
              Set up your account to join the team.
            </p>

            <form onSubmit={handleAcceptAsNewUser}>
              <div className="invite-field">
                <label>Email</label>
                <input
                  type="email"
                  value={invitation?.email}
                  disabled
                  className="invite-input invite-input--disabled"
                />
              </div>

              <div className="invite-field">
                <label>Username <span className="invite-optional">(optional)</span></label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Choose a username"
                  className="invite-input"
                />
              </div>

              <div className="invite-field">
                <label>Password</label>
                <div className="invite-input-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password (min. 6 characters)"
                    className="invite-input"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="invite-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <div className="invite-field">
                <label>Confirm Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="invite-input"
                  required
                />
              </div>

              <button
                type="submit"
                className="invite-btn invite-btn--primary"
                disabled={accepting}
              >
                {accepting ? (
                  <span className="invite-btn-loading" />
                ) : (
                  'Create Account & Join Team'
                )}
              </button>
            </form>
          </div>
        )}

        {/* Footer */}
        <div className="invite-footer">
          <p>
            This invitation expires on{' '}
            <strong>
              {new Date(invitation?.expiresAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
